import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Statistic,
  Tag,
  Typography,
  Button,
  message,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Switch,
  Space,
  Tooltip,
} from "antd";
import type { TableProps } from "antd";
import { useGlobal } from "../../contexts/GlobalContext";
import {
  VIP_LEVEL_OPTIONS,
  PLAN_LABEL_CN,
  PLAN_COLOR,
} from "../../utils/plan";
import request from "../../utils/request";
import dayjs from "dayjs";

const { Text } = Typography;

// 商品类型在商业化体系中的语义（四档会员 + Token 两层计费 + 奖品/返利）
const TYPE_META: Record<
  Product["type"],
  { color: string; text: string; hint: string }
> = {
  monthly: {
    color: "blue",
    text: "会员时长",
    hint: "订阅类：购买后按天数授予对应会员档位（免费/Pro/Pro Max/商业版）",
  },
  token: {
    color: "green",
    text: "Token 加购包",
    hint: "加购类：购买后向钱包充值 Token，用于超额计量消费，不改变会员档位",
  },
  reward: {
    color: "gold",
    text: "奖品 / 返利",
    hint: "赠品类：用于邀请返利等场景，不单独售卖、不授予会员档位",
  },
};

interface Product {
  id: number;
  name: string;
  code: string;
  type: "monthly" | "token" | "reward";
  count: number;
  price: number;
  original_price: number;
  image_url: string;
  description: string;
  detail_info: string;
  vip_level: number;
  discount_type: string;
  discount_rate: number;
  discount_desc: string;
  discount_start: string;
  discount_end: string;
  created_at: string;
  updated_at: string;
  reward_product: string;
  is_on_sale: boolean;
}

// 修改商品表单的数据类型
interface ProductFormData {
  name: string;
  code: "P" | "S";
  type: "monthly" | "token" | "reward";
  count: number;
  price: number;
  original_price: number;
  image_url?: string; // 改为可选
  description?: string; // 改为可选
  detail_info?: string; // 改为可选
  vip_level: number;
  discount_type: string;
  discount_rate: number;
  discount_desc: string;
  discount_start: string;
  discount_end: string;
  reward_product?: number; // 新增返利商品ID字段
  is_on_sale: boolean;
}

const ProductManagement: React.FC = () => {
  const { API_BASE_URL, token } = useGlobal();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [rewardProducts, setRewardProducts] = useState<Product[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState<"add" | "edit">("add");
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
  const [filterType, setFilterType] = useState<string>("all");
  const [filterSale, setFilterSale] = useState<string>("all");
  const [keyword, setKeyword] = useState<string>("");
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // 获取商品列表
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        is_on_sale: filterSale,
        type: filterType === "all" ? undefined : filterType,
      };

      const response = await request.get(`${API_BASE_URL}/api/v3/recharge/products`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setProducts(response.data.data.list);
      } else {
        message.error(response.data.msg || "获取商品列表失败");
      }
    } catch (error) {
      message.error("获取商品列表失败");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, token, filterType, filterSale]);

  // 获取奖品商品列表
  const fetchRewardProducts = useCallback(async () => {
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/recharge/products`, {
        params: {
          type: "reward",
          is_on_sale: "all",
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setRewardProducts(response.data.data.list);
      } else {
        message.error(response.data.msg || "获取奖品列表失败");
      }
    } catch (error) {
      message.error("获取奖品列表失败");
    }
  }, [API_BASE_URL, token]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // 在打开模态框时获取奖品列表
  useEffect(() => {
    if (modalType === "add" || modalType === "edit") {
      fetchRewardProducts();
    }
  }, [modalType, fetchRewardProducts]);

  // 处理删除商品
  const handleDelete = (record: Product) => {
    Modal.confirm({
      title: "确认删除",
      content: `确定要删除商品"${record.name}"吗？此操作不可恢复。`,
      okText: "确认删除",
      cancelText: "取消",
      okButtonProps: {
        danger: true,
      },
      onOk: async () => {
        try {
          const response = await request.post(
            `${API_BASE_URL}/api/v3/admin/goods/delete`,
            {
              id: record.id,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.data.code === 200) {
            message.success("商品删除成功");
            fetchProducts(); // 刷新商品列表
          } else {
            message.error(response.data.msg || "商品删除失败");
          }
        } catch (error) {
          message.error("商品删除失败，请重试");
        }
      },
    });
  };

  // 快速上/下架：直接回写原始记录并翻转上架状态，无需打开编辑弹窗
  const handleToggleSale = async (record: Product) => {
    setTogglingId(record.id);
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/admin/goods/update`,
        {
          ...record,
          // type 为数值枚举，改走 type_str 字符串，避免字符串类型导致后端绑定报错
          type: undefined,
          type_str: record.type,
          is_on_sale: !record.is_on_sale,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success(record.is_on_sale ? "商品已下架" : "商品已上架");
        fetchProducts();
      } else {
        message.error(response.data.msg || "操作失败");
      }
    } catch (error) {
      message.error("操作失败，请重试");
    } finally {
      setTogglingId(null);
    }
  };

  const columns: TableProps<Product>["columns"] = [
    {
      title: "商品名称",
      dataIndex: "name",
      key: "name",
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: "类型",
      dataIndex: "type",
      key: "type",
      render: (type: Product["type"]) => {
        const meta = TYPE_META[type] ?? TYPE_META.reward;
        return (
          <Tooltip title={meta.hint}>
            <Tag color={meta.color}>{meta.text}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "授予档位",
      key: "vip_level",
      render: (_, record: Product) =>
        record.type === "monthly" ? (
          <Tag color={PLAN_COLOR[record.vip_level] ?? "default"}>
            {PLAN_LABEL_CN[record.vip_level] ?? "免费"}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "状态",
      dataIndex: "is_on_sale",
      key: "is_on_sale",
      render: (is_on_sale: boolean, record: Product) => (
        <Switch
          checked={is_on_sale}
          loading={togglingId === record.id}
          checkedChildren="上架"
          unCheckedChildren="下架"
          onChange={() => handleToggleSale(record)}
        />
      ),
    },
    {
      title: "数量",
      dataIndex: "count",
      key: "count",
      render: (count: number, record: Product) =>
        `${count.toLocaleString()}${
          record.type === "monthly"
            ? "天"
            : record.type === "token"
            ? " tokens"
            : ""
        }`,
    },
    {
      title: "价格",
      key: "price",
      render: (_, record: Product) => (
        <div>
          <Text type="danger">¥{(record.price / 100).toFixed(2)}</Text>
          {record.original_price !== record.price && (
            <Text delete type="secondary" style={{ marginLeft: 8 }}>
              ¥{(record.original_price / 100).toFixed(2)}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "返利商品",
      key: "reward_product",
      render: (_, record: Product) => {
        if (record.type === "reward") return "-";
        if (record.reward_product === "none") return "无";
        const rewardProduct = rewardProducts.find(
          (p) => p.id === Number(record.reward_product)
        );
        return rewardProduct ? rewardProduct.name : "-";
      },
    },
    {
      title: "优惠信息",
      key: "discount",
      render: (_, record: Product) =>
        record.discount_type === "none" ? (
          "-"
        ) : record.discount_desc ? (
          <div>
            <Tag color="orange">{record.discount_desc}</Tag>
            <div style={{ fontSize: "12px", color: "#999" }}>
              {dayjs(record.discount_start).format("YYYY-MM-DD")} 至{" "}
              {dayjs(record.discount_end).format("YYYY-MM-DD")}
            </div>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "操作",
      key: "action",
      render: (_, record: Product) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => handleEdit(record)}
            style={{ padding: 0 }}
          >
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => handleDelete(record)}
            style={{ color: "#ff4d4f", padding: 0 }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 处理编辑商品
  const handleEdit = (record: Product) => {
    setCurrentProduct(record);
    setModalType("edit");
    // 设置表单初始值，注意价格需要从分转换为元
    form.setFieldsValue({
      ...record,
      type: record.type,
      price: record.price / 100,
      original_price: record.original_price / 100,
      discount_start: record.discount_start
        ? dayjs(record.discount_start)
        : undefined,
      discount_end: record.discount_end
        ? dayjs(record.discount_end)
        : undefined,
      reward_product:
        record.reward_product === "none"
          ? undefined
          : Number(record.reward_product),
    });
    setModalVisible(true);
  };

  // 处理打开新增商品弹窗
  const handleAdd = () => {
    setModalType("add");
    form.resetFields();
    form.setFieldsValue({
      discount_type: "discount",
      is_on_sale: true,
    });
    setModalVisible(true);
  };

  // 处理表单提交
  const handleSubmit = async (values: ProductFormData) => {
    setSubmitting(true);
    try {
      // 构造符合 API 格式的请求数据
      const requestData = {
        ...values,
        // 如果是编辑模式，添加商品ID
        ...(modalType === "edit" && currentProduct
          ? { id: currentProduct.id }
          : {}),
        // 转换时间格式
        discount_start: values.discount_start
          ? dayjs(values.discount_start).format()
          : undefined,
        discount_end: values.discount_end
          ? dayjs(values.discount_end).format()
          : undefined,
        // 转换价格为分
        price: Math.round(values.price * 100),
        original_price: Math.round(values.original_price * 100),
        // 确保 reward_product 是字符串类型
        reward_product: values.reward_product
          ? String(values.reward_product)
          : "none",
        // 如果是奖品类型，设置相关字段为默认值
        discount_type: values.type === "reward" ? "none" : values.discount_type,
        discount_rate: values.type === "reward" ? 0 : values.discount_rate,
        discount_desc: values.type === "reward" ? "" : values.discount_desc,
        // 仅「会员时长」商品授予会员档位；Token 加购包 / 奖品 不改变档位，统一存 0
        vip_level: values.type === "monthly" ? values.vip_level ?? 0 : 0,
        // 商品类型按后端约定走 type_str（字符串枚举）；数值型 type 字段置空交由后端推导
        type: undefined,
        type_str: values.type,
        // 设置上架状态
        is_on_sale: values.is_on_sale ?? true,
      };

      const url =
        modalType === "add"
          ? `${API_BASE_URL}/api/v3/admin/goods/add`
          : `${API_BASE_URL}/api/v3/admin/goods/update`;

      const response = await request.post(url, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        message.success(`商品${modalType === "add" ? "添加" : "更新"}成功`);
        setModalVisible(false);
        form.resetFields();
        setCurrentProduct(null);
        fetchProducts(); // 刷新商品列表
      } else {
        message.error(
          response.data.msg ||
            `商品${modalType === "add" ? "添加" : "更新"}失败`
        );
      }
    } catch (error) {
      message.error(`商品${modalType === "add" ? "添加" : "更新"}失败，请重试`);
    } finally {
      setSubmitting(false);
    }
  };

  // 商品表单弹窗
  const productModal = (
    <Modal
      title={modalType === "add" ? "新增商品" : "编辑商品"}
      open={modalVisible}
      onCancel={() => {
        setModalVisible(false);
        form.resetFields();
        setCurrentProduct(null);
      }}
      footer={null}
      width={800}
      maskClosable={false}
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: "calc(100vh - 200px)",
          overflow: "auto",
          paddingRight: "10px",
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ paddingRight: "10px" }}
      >
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              name="name"
              label="商品名称"
              rules={[{ required: true, message: "请输入商品名称" }]}
            >
              <Input placeholder="请输入商品名称" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="is_on_sale"
              label="商品状态"
              valuePropName="checked"
            >
              <Switch checkedChildren="上架" unCheckedChildren="下架" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="type"
              label="商品类型"
              rules={[{ required: true, message: "请选择商品类型" }]}
            >
              <Select
                placeholder="请选择商品类型"
                onChange={(value) => {
                  if (value === "reward") {
                    form.setFieldsValue({
                      reward_product: undefined,
                      discount_type: "none",
                    });
                  }
                }}
              >
                <Select.Option value="monthly">会员时长</Select.Option>
                <Select.Option value="token">Token 加购包</Select.Option>
                <Select.Option value="reward">奖品 / 返利</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.type !== currentValues.type
              }
            >
              {({ getFieldValue }) => (
                <Form.Item
                  name="count"
                  label={
                    getFieldValue("type") === "monthly"
                      ? "会员时长（天）"
                      : getFieldValue("type") === "token"
                      ? "Token 数量"
                      : "赠送 Token 数量"
                  }
                  rules={[{ required: true, message: "请输入数量" }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder={
                      getFieldValue("type") === "monthly"
                        ? "请输入会员天数"
                        : "请输入 Token 数量"
                    }
                    min={1}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.type !== currentValues.type
              }
            >
              {({ getFieldValue }) =>
                getFieldValue("type") === "monthly" ? (
                  <Form.Item
                    name="vip_level"
                    label="授予会员档位"
                    tooltip="购买该商品后授予用户的会员档位，按四档会员体系生效"
                    rules={[{ required: true, message: "请选择会员档位" }]}
                  >
                    <Select
                      style={{ width: "100%" }}
                      placeholder="购买后的会员档位"
                      options={VIP_LEVEL_OPTIONS}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item
                    label="授予会员档位"
                    tooltip="Token 加购包仅向钱包充值，奖品用于返利赠送，均不改变会员档位"
                  >
                    <Input disabled value="不授予档位（仅充值 / 赠送）" />
                  </Form.Item>
                )
              }
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="price"
              label="售价(元)"
              rules={[{ required: true, message: "请输入售价" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="请输入售价"
                min={0}
                precision={2}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="original_price"
              label="原价(元)"
              rules={[{ required: true, message: "请输入原价" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="请输入原价"
                min={0}
                precision={2}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="商品描述"
          rules={[{ required: true, message: "请输入商品描述" }]}
        >
          <Input.TextArea placeholder="请输入商品描述" rows={2} />
        </Form.Item>

        <Form.Item
          name="image_url"
          label="商品图片"
          rules={[{ type: "url", message: "请输入有效的URL" }]}
        >
          <Input placeholder="请输入商品图片URL（可选）" />
        </Form.Item>

        <Form.Item name="detail_info" label="商品详情">
          <Input.TextArea placeholder="请输入商品详情（可选）" rows={3} />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.type !== currentValues.type
          }
        >
          {({ getFieldValue }) => (
            <Form.Item
              name="reward_product"
              label="返利商品"
              hidden={getFieldValue("type") === "reward"}
            >
              <Select placeholder="请选择返利商品（可选）">
                <Select.Option value={undefined}>无</Select.Option>
                {rewardProducts && rewardProducts.length > 0 ? (
                  rewardProducts.map((product) => (
                    <Select.Option key={product.id} value={product.id}>
                      {product.name}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option value={undefined} disabled>
                    暂无可选返利商品
                  </Select.Option>
                )}
              </Select>
            </Form.Item>
          )}
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.type !== currentValues.type
          }
        >
          {({ getFieldValue }) => (
            <Form.Item
              name="discount_type"
              label="优惠类型"
              hidden={getFieldValue("type") === "reward"}
            >
              <Select>
                <Select.Option value="discount">折扣</Select.Option>
                <Select.Option value="none">无优惠</Select.Option>
              </Select>
            </Form.Item>
          )}
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.discount_type !== currentValues.discount_type
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("discount_type") === "discount" ? (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="discount_rate"
                      label="折扣率"
                      tooltip="0-1 之间，例如 0.8 表示 8 折；用于活动展示，实际成交价以「售价」为准"
                      rules={[{ required: true, message: "请输入折扣率" }]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="如 0.8 = 8折"
                        min={0}
                        max={1}
                        step={0.1}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="discount_desc"
                      label="优惠描述"
                      rules={[{ required: true, message: "请输入优惠描述" }]}
                    >
                      <Input placeholder="例如：限时8折优惠" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="discount_start"
                      label="优惠开始时间"
                      rules={[{ required: true, message: "请选择开始时间" }]}
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        showTime
                        format="YYYY-MM-DD HH:mm:ss"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="discount_end"
                      label="优惠结束时间"
                      rules={[{ required: true, message: "请选择结束时间" }]}
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        showTime
                        format="YYYY-MM-DD HH:mm:ss"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ) : null
          }
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} block>
            {modalType === "add" ? "确认添加" : "确认更新"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      {/* 统计数据 */}
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card style={{ height: "100%" }} variant="outlined">
            <Statistic
              title={
                <>
                  总商品数 <i className="ri-shopping-bag-line" />
                </>
              }
              value={products.length}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card style={{ height: "100%" }} variant="outlined">
            <Statistic
              title={
                <>
                  在售商品 <i className="ri-checkbox-circle-line" />
                </>
              }
              value={products.filter((p) => p.is_on_sale).length}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card style={{ height: "100%" }} variant="outlined">
            <Statistic
              title={
                <>
                  会员商品 <i className="ri-vip-crown-line" />
                </>
              }
              value={products.filter((p) => p.type === "monthly").length}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card style={{ height: "100%" }} variant="outlined">
            <Statistic
              title={
                <>
                  Token商品 <i className="ri-coin-line" />
                </>
              }
              value={products.filter((p) => p.type === "token").length}
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选条件 */}
      <Card
        title={
          <span>
            <i
              className="ri-filter-3-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            ></i>
            筛选条件
          </span>
        }
        style={{ marginTop: "16px", marginBottom: "16px" }}
        variant="outlined"
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Select
              value={filterType}
              onChange={(value) => {
                setFilterType(value);
              }}
              style={{ width: "100%" }}
              placeholder="选择商品类型"
            >
              <Select.Option value="all">全部类型</Select.Option>
              <Select.Option value="monthly">会员时长</Select.Option>
              <Select.Option value="token">Token 加购包</Select.Option>
              <Select.Option value="reward">奖品 / 返利</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={filterSale}
              onChange={(value) => {
                setFilterSale(value);
              }}
              style={{ width: "100%" }}
              placeholder="上架状态"
            >
              <Select.Option value="all">全部状态</Select.Option>
              <Select.Option value="true">已上架</Select.Option>
              <Select.Option value="false">已下架</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={7}>
            <Input
              allowClear
              prefix={<i className="ri-search-line" style={{ color: "#bbb" }} />}
              placeholder="搜索商品名称"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={5} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<i className="ri-add-line" />}
              onClick={handleAdd}
            >
              新增商品
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 商品列表 */}
      <Card
        title={
          <span>
            <i
              className="ri-shopping-cart-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            ></i>
            商品列表
          </span>
        }
        variant="outlined"
        styles={{
          body: { padding: 8 },
        }}
      >
        <Table<Product>
          columns={columns}
          dataSource={
            keyword.trim()
              ? products.filter((p) =>
                  p.name.toLowerCase().includes(keyword.trim().toLowerCase())
                )
              : products
          }
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 件商品`,
          }}
        />
      </Card>

      {productModal}
    </div>
  );
};

export default ProductManagement;
